import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import api from '@api/apiServices';
import i18n from '@i18n';

export const settingsQueryOptions = () => {
    return queryOptions({
        queryKey: ['settings'],
        queryFn: () =>
            api.settings.get().then(res => {
                i18n.changeLanguage(res.language);
                return res;
            }),
    });
};

export const subscriptionsQueryOptions = () =>
    queryOptions({
        queryKey: ['subscription'],
        queryFn: () => api.subscription.get(),
    });

export const pricesQueryOptions = () =>
    queryOptions({
        queryKey: ['prices'],
        queryFn: () => api.settings.billing.prices.get(),
    });

export const contactsQueryOptions = params =>
    queryOptions({
        queryKey: ['contacts', params],
        queryFn: () => api.contacts.get(params),
    });

export const contactsMobileQueryOptions = params =>
    infiniteQueryOptions({
        queryKey: ['contacts', params],
        queryFn: ({ pageParam }) =>
            api.contacts.get({ page: pageParam, page_size: 200, ...params }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => {
            if (lastPage.next) return pages.length + 1;
            return undefined;
        },
    });

export const contactQueryOptions = id =>
    queryOptions({
        queryKey: ['contact', id],
        queryFn: () => api.contacts.get(id),
        // gcTime: 0,
    });

export const contactActivitiesQueryOptions = id =>
    queryOptions({
        queryKey: ['contactActivities', id],
        queryFn: () => api.contacts[id].activities.get(),
    });

export const donationsQueryOptions = params =>
    queryOptions({
        queryKey: ['donations', params],
        queryFn: () => api.donations.get(params),
    });

export const expensesQueryOptions = params =>
    queryOptions({
        queryKey: ['expenses', params],
        queryFn: () => api.expenses.get(params),
    });

export const emailsSentQueryOptions = params =>
    queryOptions({
        queryKey: ['emailsSent', params],
        queryFn: () => api.emails.sent.get(params),
    });

export const globalEmailTemplatesQueryOptions = () => {
    const lang = i18n.language;
    return queryOptions({
        queryKey: ['globalEmailTemplates', lang],
        queryFn: () => api.emails.templates.global[lang].get(),
    });
};

export const emailTemplatesQueryOptions = params =>
    queryOptions({
        queryKey: ['emailTemplates', params],
        queryFn: ({ pageParam: page }) =>
            api.emails.templates.get({ page, ...params }),
    });

export const emailTemplateVariantsQueryOptions = (templateId, params) =>
    queryOptions({
        queryKey: ['emailTemplateVariants', templateId, params],
        enabled: Boolean(templateId),
        queryFn: ({ pageParam: page }) =>
            api.emails.templates[templateId].variants.get({ page, ...params }),
    });

export const emailTemplateQueryOptions = id =>
    queryOptions({
        queryKey: ['emailTemplate', id],
        queryFn: () => api.emails.templates.get(id),
        gcTime: 0,
    });

export const emailDesignsQueryOptions = params =>
    queryOptions({
        queryKey: ['emailDesigns', params],
        queryFn: ({ pageParam }) =>
            api.emails.designs.get({ page: pageParam, ...params }),
    });

export const emailDesignQueryOptions = id =>
    queryOptions({
        queryKey: ['emailDesign', id],
        queryFn: () => api.emails.designs.get(id),
        gcTime: 0,
    });

export const emailsScheduledQueryOptions = params =>
    queryOptions({
        queryKey: ['emailsScheduled', params],
        queryFn: () => api.emails.scheduled.get(params),
    });

export const emailScheduledQueryOptions = id =>
    queryOptions({
        queryKey: ['emailScheduled', id],
        queryFn: () => api.emails.scheduled.get(id),
        gcTime: 0,
    });

export const emailsDraftsQueryOptions = params =>
    queryOptions({
        queryKey: ['emailsDrafts', params],
        queryFn: () => api.emails.drafts.get(params),
    });

export const emailDraftQueryOptions = id =>
    queryOptions({
        queryKey: ['emailDraft', id, window.location.pathname],
        queryFn: () => api.emails.drafts.get(id),
        gcTime: 0,
    });

export const fromEmailsQueryOptions = () =>
    queryOptions({
        queryKey: ['fromEmails'],
        queryFn: () => api['from-emails'].get(),
    });

export const emailSentQueryOptions = id =>
    queryOptions({
        queryKey: ['emailSent', id],
        queryFn: () => api.emails.sent.get(id),
        gcTime: 0,
    });

export const emailRecipientsQueryOptions = (id, params = {}) =>
    queryOptions({
        queryKey: ['emailStats', id, params],
        queryFn: () => api.emails.sent.stats.recipients[id].get(params),
    });

export const emailStatsQueryOptions = (id, statType, params = {}) =>
    queryOptions({
        queryKey: ['emailStats', id, params],
        queryFn: () =>
            api.emails.sent.stats.recipients[id][statType].get(params),
    });

export const contactFiltersQueryOptions = () =>
    queryOptions({
        queryKey: ['contactFilters'],
        queryFn: () => api.contacts.filters.get(),
    });

export const contactsGroupsQueryOptions = params =>
    queryOptions({
        queryKey: ['contactsGroups', params],
        queryFn: () => api.contacts.groups.get(params),
    });

export const contactsGroupQueryOptions = id =>
    queryOptions({
        queryKey: ['contactsGroup', id],
        queryFn: () => api.contacts.groups.get(id),
        gcTime: 0,
    });

export const contactsGroupItemsQueryOptions = (groupId, params) =>
    queryOptions({
        queryKey: ['contactsGroupItems', groupId, params],
        queryFn: () => api.contacts.groups[groupId].contacts.get(params),
    });

export const emailRecipientsContactsQueryOptions = (emailId, params = {}) => {
    const queryParams =
        emailId === 'new'
            ? 'new'
            : {
                  recipients__email: emailId,
                  'contact_group_item__group__recipients__email|': emailId,
                  page_size: 1,
                  ...params,
              };
    return queryOptions({
        queryKey: ['emailRecipientsContacts', emailId, params],
        queryFn: () => api.contacts.get(queryParams),
    });
};
